import { InfoCircleFilled } from '@ant-design/icons';
import { Alert, Divider, List, Popover, Spin, Tag, Typography } from 'antd';
import dayjs from 'dayjs';
import _ from 'lodash';
import { ArrowLeft, Search, UserRoundPen } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import ChatLayout from '../../Layout/ChatLayout';
import ChatInput from '../../components/ChatInput';
import MessageBubble from '../../components/MessageBubble';
import SearchDrawer from '../../components/SearchDrawer';
import { sender_number } from '../../components/config';
import { getRoutes } from '../../constants/Routes';
import { getMessages } from '../../redux/actions/message';
import { getFullName } from '../../utils';
import { decodeId } from '../../utils/secureId';
import { bannerMessages } from '../../constants';
import ContactModal from '../../components/ContactModal';

const ChatDetailScreen = () => {
    const { userId: encUserId } = useParams();
    const userId = encUserId && decodeId(encUserId);
    const navigate = useNavigate();
    const {
        messages,
        messagesPagination: pagination,
        activeUser,
    } = useSelector((state) => state.messageSlice);

    const [loading, setLoading] = useState(false);
    const [isSearch, setIsSearch] = useState(false);
    const [highlightMessageId, setSetHighlightMessageId] = useState(null);
    const [contact, setContact] = useState(null);

    const messageRefs = useRef({});
    const isBlocked = activeUser?.status === 0;

    const isCustomerMessage = (msg) => msg.from_number !== sender_number;
    const customerMessages = _.filter(messages, isCustomerMessage);
    const latestCustomerMsg = _.last(customerMessages);
    const isOlderThan24Hours = latestCustomerMsg
        ? dayjs().diff(dayjs(latestCustomerMsg.createdAt), 'hour') > 24
        : true;

    const getMoreMessages = () => {
        getMessages(userId, false, pagination?.page + 1).then(() => {
            setLoading(false);
        });

        if (pagination.page < pagination?.totalPages && !loading) {
            setLoading(true);
            getMessages(userId, false, pagination?.page + 1).then(() => {
                setLoading(false);
            });
        }
    };

    const scrollToMessage = (messageId) => {
        const messageElement = messageRefs.current[messageId];
        if (messageElement) {
            messageElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
        setSetHighlightMessageId(messageId);
        setIsSearch(false);
    };

    const messageInfo = (
        <List
            size="small"
            dataSource={[
                { label: 'Name', value: activeUser?.name || 'N/A' },
                { label: 'Phone', value: activeUser?.phone || 'N/A' },
                activeUser?.netsuite_customer && {
                    label: 'Customer',
                    value: `(${activeUser?.netsuite_customer?.code}) ${activeUser?.netsuite_customer?.name}`,
                },
                activeUser?.assigned_user && {
                    label: 'Assigned',
                    value: getFullName(activeUser?.assigned_user),
                },
                isBlocked && {
                    label: 'Status',
                    value: <Tag color="#d13438">Blocked</Tag>,
                },
            ].filter(Boolean)}
            renderItem={(item) => (
                <List.Item>
                    <span style={{ fontWeight: 600, minWidth: 70 }}>{item.label}:</span>
                    <span style={{ marginLeft: 8 }}>{item.value}</span>
                </List.Item>
            )}
        />
    );

    useEffect(() => {
        getMessages(userId);
    }, [userId]);

    // Group messages by date
    const groupedMessages = useMemo(() => {
        return messages?.reduce((acc, message) => {
            const dateKey = dayjs(message.createdAt).format('YYYY-MM-DD');
            if (!acc[dateKey]) {
                acc[dateKey] = [];
            }
            acc[dateKey].push(message);
            return acc;
        }, {});
    }, [messages]);

    return (
        <ChatLayout className="CHAT_DETAIL">
            <div className="Chat__Header">
                <div className="header-left">
                    <ArrowLeft
                        size={20}
                        className="back-btn"
                        onClick={() => navigate(getRoutes().chats.route())}
                    />
                    <div className="info-container">
                        <div className="info">
                            <div className="subtitle">
                                <Typography.Text ellipsis={{ tooltip: true }}>
                                    {activeUser?.name || activeUser?.phone || 'Unknown'}
                                </Typography.Text>
                            </div>
                            <div className="title">{activeUser?.phone}</div>
                        </div>
                        {isBlocked && (
                            <>
                                <Divider className="custom-divider" type="vertical" />
                                <div className="status">
                                    <Tag color="#d13438">Blocked</Tag>
                                </div>
                            </>
                        )}
                        {activeUser && (
                            <>
                                <Divider className="custom-divider" type="vertical" />
                                <UserRoundPen
                                    size={26}
                                    color="rgb(156,163,175)"
                                    cursor="pointer"
                                    onClick={() => setContact({
                                        ...activeUser,
                                        customer_code: activeUser?.netsuite_customer?.code || '',
                                        customer_name: activeUser?.netsuite_customer?.name || '',
                                    })}
                                />
                            </>
                        )}
                    </div>
                </div>
                <div className="header-right">
                    <div className="info-container">
                        {activeUser?.netsuite_customer && (
                            <>
                                <div className="info">
                                    <div className="title">Customer</div>
                                    <div className="subtitle">
                                        <Typography.Text ellipsis={{ tooltip: true }}>
                                            ({activeUser?.netsuite_customer?.code}) {activeUser?.netsuite_customer?.name}
                                        </Typography.Text>
                                    </div>
                                </div>
                                <Divider className="custom-divider" type="vertical" />
                            </>
                        )}
                        {activeUser?.assigned_user && (
                            <>
                                <div className="info">
                                    <div className="title">Assigned User</div>
                                    <div className="subtitle">
                                        <Typography.Text ellipsis={{ tooltip: true }}>
                                            {getFullName(activeUser?.assigned_user)}
                                        </Typography.Text>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    <Popover content={messageInfo} trigger="click">
                        <InfoCircleFilled className="info-popover" />
                    </Popover>

                    <Search
                        size={22}
                        color="rgb(156,163,175)"
                        cursor="pointer"
                        onClick={() => setIsSearch(true)}
                    />
                </div>
            </div>
            <div
                id="scrollableDiv"
                style={{
                    overflow: 'auto',
                    display: 'flex',
                    flexDirection: 'column-reverse',
                    height: '500px',
                }}
                className="chat-messages"
            >
                <InfiniteScroll
                    dataLength={messages?.length}
                    next={getMoreMessages}
                    hasMore={pagination?.page < pagination?.totalPages}
                    inverse={true}
                    scrollableTarget="scrollableDiv"
                >
                    {loading && (
                        <div className="chat-loader">
                            <p>Loading more messages...</p>
                            <Spin size="small" />
                        </div>
                    )}
                    {pagination?.totalPages > 1 && pagination?.page === pagination?.totalPages && (
                        <Alert message="You've reached the end of the chat history! 🎉" closable showIcon />
                    )}
                    {groupedMessages &&
                        Object.keys(groupedMessages).map((date) => (
                            <div key={date}>
                                <div className="date-separator-container">
                                    <div className="date">
                                        {dayjs(date).isSame(dayjs(), 'day')
                                            ? 'Today'
                                            : dayjs(date).format('DD/MM/YYYY')}
                                    </div>
                                </div>
                                {groupedMessages[date].map(
                                    (message) =>
                                        message?.type !== 0 && (
                                            <MessageBubble
                                                key={message.id}
                                                message={message}
                                                user={activeUser}
                                                isHighlighted={highlightMessageId === message.id}
                                                ref={(el) => (messageRefs.current[message.id] = el)}
                                            />
                                        )
                                )}
                            </div>
                        ))}
                </InfiniteScroll>
            </div>
            {activeUser && (isBlocked || isOlderThan24Hours) && (
                <Alert
                    key={userId}
                    message={isBlocked ? bannerMessages.BLOCKED : bannerMessages.OLDER_THAN_24_HOURS}
                    banner
                    closable
                    showIcon={false}
                />
            )}
            <ChatInput disabled={isOlderThan24Hours} isBlocked={isBlocked} sentTo={activeUser} />
            {isSearch && (
                <SearchDrawer
                    open={isSearch}
                    onClose={() => setIsSearch(false)}
                    onClick={(message) => scrollToMessage(message?.id)}
                />
            )}
            {!!contact && (
                <ContactModal
                    open={!!contact}
                    onCancel={() => setContact(null)}
                    data={contact}
                />
            )}
        </ChatLayout>
    );
};

export default ChatDetailScreen;
